// ── Choosing and buying a plan ──
// Presentational: renders what it is given and calls back. Every amount and every sentence
// about renewal comes from the server's quote and is printed AS IT ARRIVES. This file does not
// compute money and does not reword the terms — if it did, the page and the order would be
// able to disagree about what was bought.
import React, { useEffect, useRef, useState } from 'react';
import { INR } from './plans.api.js';

import { card, h2, sub, field, primaryBtn, ghostBtn, label, mono, note } from './billing.styles.js';
import ChangePlan from './ChangePlan.jsx';

// A GSTIN's own check (format + mod-36 check character), so a typo is caught here; the server checks it again and
// is the one that counts (src/plans/gstin.js).
const GST_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
export function gstinOk(g) {
  if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(g)) return false;
  let sum = 0;
  for (let i = 0; i < 14; i++) { const p = GST_CHARS.indexOf(g[i]) * (i % 2 ? 2 : 1); sum += Math.floor(p / 36) + (p % 36); }
  return GST_CHARS[(36 - (sum % 36)) % 36] === g[14];
}

export default function PlanPicker({ plans, features, states, selling, subscription, quote, busy, onQuote, onPay, onClearQuote,
  onDowngrade, onCancelDowngrade, defaultPhone, onSetupAction, preselect }) {
  const [plan, setPlan] = useState(null);
  const [product, setProduct] = useState('monthly');
  const [billingState, setBillingState] = useState('');
  const [gstin, setGstin] = useState('');
  const [legalName, setLegalName] = useState('');
  const [address, setAddress] = useState('');
  const gstinInput = useRef(null), legalInput = useRef(null), addressInput = useRef(null);
  const [promo, setPromo] = useState('');
  // What is still missing when they press See the total: said next to the button, the field outlined and focused.
  const [missing, setMissing] = useState(null);   // null | 'plan' | 'state' | 'gstin' | 'legalName' | 'address'
  const planGroup = useRef(null), stateSelect = useRef(null), phoneInput = useRef(null);
  const [phoneMissing, setPhoneMissing] = useState(false);
  const [phone, setPhone] = useState(defaultPhone || '');
  useEffect(() => { if (defaultPhone) setPhone((p) => p || defaultPhone); }, [defaultPhone]);   // the verified mobile

  // /billing#renew and /billing#change (Membership, the reminder emails): the sections render after the
  // data loads, so the browser's own jump to the anchor has already happened and found nothing.
  const hasPlan = !!(subscription && subscription.change);
  useEffect(() => {
    const id = (window.location.hash || '').slice(1);
    if (!hasPlan || !['renew', 'change'].includes(id)) return;
    const el = document.getElementById(id);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'start' });
  }, [hasPlan]);

  // /billing?plan=pro&billing=annual (the pricing page's Get buttons, via sign-up): the choice is already
  // made, so it is picked for them and the one thing still needed for a total — their state — gets the
  // focus. A plan key we do not sell is ignored. Somebody already on a plan never reaches this screen
  // (ChangePlan below), so this cannot start a second purchase.
  const preselected = useRef(false);
  useEffect(() => {
    if (preselected.current || !preselect || !plans || !plans.length) return;
    preselected.current = true;
    if (!plans.some((p) => p.key === preselect.plan)) return;
    setPlan(preselect.plan);
    setProduct(preselect.product === 'annual' ? 'annual' : 'monthly');
    setTimeout(() => {
      const el = stateSelect.current;
      if (!el) return;
      if (el.scrollIntoView) el.scrollIntoView({ block: 'center' });
      el.focus();
    }, 0);
  }, [preselect, plans]);

  // Ordered deliberately: a quote in hand is rendered BEFORE the catalogue is consulted. The
  // catalogue is only needed to CHOOSE a plan, and a transient failure to reload it must not
  // take a live quote off the screen — the quote is held in memory, so losing it loses the
  // customer's place entirely.
  if (quote) return renderQuote();

  if (!plans || !plans.length) return null;

  // NOTHING CAN BE BOUGHT RIGHT NOW. `selling` is the server's own answer: a usable gateway AND
  // at least one priced plan. Somebody who already has a subscription is told about theirs
  // instead — their plan is not affected by our inability to sell a new one.
  if (selling === false && !(subscription && subscription.entitled)) {
    return (
      <section style={card}>
        <h2 style={h2}>Payments are not switched on yet</h2>
        <p style={sub}>Nothing here can charge you. This is where you will choose a plan once it is live.</p>
      </section>
    );
  }

  // A purchase in flight. The payment is taken as the customer authorises, but the gateway
  // confirms it by webhook a moment later — so without this they come back from their bank,
  // see the plan list again, and reasonably conclude nothing happened.
  const setup = subscription && subscription.setupInProgress;
  if (setup && !(subscription.entitled && subscription.plan)) {
    const p = plans.find((x) => x.key === setup.plan);
    return (
      <section style={card}>
        <h2 style={h2}>{setup.authorised ? 'Your plan is being set up' : 'Waiting for your bank'}</h2>
        <p style={sub}>
          {setup.authorised
            ? `Your bank has approved the payment for ${(p && p.name) || setup.plan}. `
              + 'It is going through now, and your plan starts the moment it clears. '
              + 'Nothing more for you to do.'
            : `We are waiting for your bank to approve the payment for ${(p && p.name) || setup.plan}. `
              + 'This usually takes a few minutes. You have not been charged.'}
        </p>
        <p style={note}>{INR(setup.total)} {setup.product === 'annual' ? 'a year' : 'a month'}, tax included.</p>
        {!setup.authorised && onSetupAction && (
          <>
            {/* Closing the payment page tells nobody: without these, somebody who closed it, or wants to pay
                another way, waited here for good. */}
            <p style={{ ...sub, marginTop: 18 }}>Closed the payment page, or want to pay another way?</p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {setup.checkout && (
                <button style={primaryBtn} disabled={!!busy} onClick={() => onSetupAction('continue')}>
                  {busy === 'continue' ? 'Opening…' : 'Continue payment'}</button>
              )}
              <button style={ghostBtn} disabled={!!busy} onClick={() => onSetupAction('check')}>
                {busy === 'check' ? 'Checking…' : 'Check with my bank'}</button>
              <button style={ghostBtn} disabled={!!busy} onClick={() => onSetupAction('restart')}>
                {busy === 'restart' ? 'Cancelling…' : 'Start again'}</button>
            </div>
            <p style={note}>Start again cancels this unfinished payment, so you can choose UPI or card. Nothing has been charged.</p>
          </>
        )}
      </section>
    );
  }

  // Somebody already paying changes plan here: UP now for the difference for the days left, DOWN at the
  // next renewal, monthly → yearly, and renewing by hand (ChangePlan).
  if (subscription && subscription.entitled && subscription.plan) {
    return <ChangePlan plans={plans} features={features} states={states} subscription={subscription} busy={busy}
      onQuote={onQuote} onDowngrade={onDowngrade} onCancelDowngrade={onCancelDowngrade} />;
  }

  function renderQuote() {
    const row = { display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--border)' };
    return (
      <section style={card}>
        <h2 style={h2}>Check this before you pay</h2>
        <p style={sub}>These are the exact amounts your order was written down with.</p>

        <div style={{ margin: '0 0 18px' }}>
          <div style={row}>
            <span>{quote.upgrade ? `Move up to ${quote.planName}` : quote.switchAnnual ? `${quote.planName}, one year${quote.switchAnnual.lockIn === false ? '' : " at today's price"}`
              : quote.renewal ? `${quote.planName}, renewed ${quote.renewal.from} to ${quote.renewal.to}`
              : `${quote.planName}, ${quote.product === 'annual' ? 'one year' : 'one month'}`}</span>
            <span style={mono}>{INR(quote.discount || quote.credit ? quote.listPrice : quote.base)}</span>
          </div>
          {quote.credit && (
            <div style={row}>
              <span>Unused {quote.credit.daysLeft} day{quote.credit.daysLeft === 1 ? '' : 's'} of this month</span>
              <span style={mono}>−{INR(quote.credit.amount)}</span>
            </div>
          )}
          {quote.discount && (
            <div style={row}>
              <span>{quote.discount.label || `Code ${quote.discount.code} — ${quote.discount.sentence}`}</span>
              <span style={mono}>−{INR(quote.discount.amount)}</span>
            </div>
          )}
          {quote.taxLines.map((t) => (
            <div key={t.label} style={row}>
              <span style={{ color: 'var(--muted)' }}>{t.label}</span>
              <span style={{ ...mono, color: 'var(--muted)' }}>{INR(t.amount)}</span>
            </div>
          ))}
          <div style={{ ...row, borderBottom: 'none', fontWeight: 750, fontSize: 17, paddingTop: 14 }}>
            <span>Total</span><span style={mono}>{INR(quote.total)}</span>
          </div>
        </div>

        {/* Verbatim from the server. */}
        <p style={note}>{quote.startsSentence}</p>
        <p style={note}>{quote.renewalSentence}</p>
        <p style={note}>
          Place of supply: {quote.placeOfSupply}.{quote.priceLockedUntil ? ` Your price is held until ${quote.priceLockedUntil}.` : ''}
        </p>

        <label style={{ display: 'block', margin: '18px 0' }}>
          <span style={label}>Mobile number</span>
          <input
            ref={phoneInput} aria-invalid={phoneMissing || undefined}
            style={phoneMissing ? { ...field, border: '1px solid var(--loss)' } : field} type="tel" inputMode="numeric" autoComplete="tel" value={phone} maxLength={10}
            onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 10); setPhone(v); if (v.length === 10) setPhoneMissing(false); }}
            placeholder="10 digits"
          />
          {/* Not a formality. A made-up number makes some banks refuse cards, and the checkout
              then simply shows no card option, with nothing to explain why. */}
          <small style={{ ...note, display: 'block', marginTop: 8 }}>
            Use your real number. Some banks refuse cards otherwise.
          </small>
        </label>

        {phoneMissing && (
          <p role="alert" style={{ ...note, color: 'var(--loss)', margin: '0 0 12px' }}>Enter your 10-digit mobile number to pay.</p>
        )}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {/* THE BUTTON SAYS PAY, FOR BOTH. It used to say "Set up ₹X a month" for a mandate,
              because back then authorising took no money. It takes the full amount now — the
              approval in the customer's bank app IS the payment — and a button promising setup
              while debiting a month is how somebody disputes a charge they agreed to. That it
              also renews is said above, in the sentence from the server. */}
          {/* An AUTOPAY plan offers UPI or a card: Razorpay fixes the instrument on the autopay order, so the
              customer picks here. A one-time payment shows every method on the gateway's own page. */}
          {(quote.renewalMethod === 'mandate' ? [['upi', 'by UPI'], ['card', 'by card']] : [[undefined, '']]).map(([method, how], i) => (
            <button key={method || 'pay'} style={{ ...(i ? ghostBtn : primaryBtn), opacity: busy ? 0.55 : 1 }} disabled={busy}
              onClick={() => { if (phone.length !== 10) { setPhoneMissing(true); phoneInput.current?.focus(); return; } onPay(method ? { phone, method } : { phone }); }}>
              {busy ? 'Opening payment…' : `Pay ${INR(quote.total)}${how ? ` ${how}` : ''}`}
            </button>
          ))}
          <button style={ghostBtn} disabled={busy} onClick={onClearQuote}>Change</button>
        </div>
        <p style={{ ...note, marginTop: 14 }}>This quote is held for {quote.quoteExpiresInMinutes} minutes.</p>
      </section>
    );
  }

  const planBtn = (selected) => ({
    textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 6, cursor: 'pointer',
    padding: '18px 20px', borderRadius: 12, fontFamily: 'inherit', color: 'var(--text)',
    background: selected ? 'color-mix(in oklab, var(--accent) 12%, var(--surface2))' : 'var(--surface2)',
    border: `1px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
  });

  const comp = subscription && subscription.comp;
  const compName = comp && ((plans.find((x) => x.key === comp.plan) || {}).name || comp.plan);
  return (
    <section style={card}>
      {comp && (
        // A trial or a comp: they have it free, and can buy now to keep it when it ends.
        <p style={{ ...sub, marginTop: 0, marginBottom: 14, color: 'var(--text)' }}>
          You have {compName} free until {comp.until}. Choose a plan to keep going after that.
        </p>
      )}
      <h2 style={h2}>Choose a plan</h2>
      <p style={sub}>You can change or cancel any time. Cancelling keeps the time you have paid for.</p>

      {/* A radiogroup, not toggle buttons: these are one choice out of several, which is what
          aria-pressed does NOT mean. The monthly/yearly control below is the same shape. */}
      <div role="radiogroup" aria-label="Plan" ref={planGroup} aria-invalid={missing === 'plan' || undefined}
        style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', margin: '0 0 18px',
          ...(missing === 'plan' ? { outline: '2px solid var(--loss)', outlineOffset: 6, borderRadius: 14 } : {}) }}>
        {plans.map((p) => (
          <button key={p.key} role="radio" aria-checked={plan === p.key}
            tabIndex={plan === p.key || (!plan && p === plans[0]) ? 0 : -1}
            onKeyDown={(e) => arrowKeys(e, plans, plan, setPlan)}
            style={planBtn(plan === p.key)} onClick={() => { setPlan(p.key); if (missing === 'plan') setMissing(null); }}>
            <strong style={{ fontSize: 16.5 }}>{p.name}</strong>
            <span style={{ fontSize: 14, color: 'var(--muted)' }}>{p.tagline}</span>
            {/* Before GST: the tax is worked out for the state chosen below and shown with the total on the next step. */}
            <span style={{ ...mono, fontSize: 19, fontWeight: 750, marginTop: 6 }}>
              {INR(product === 'annual' ? p.annual.base : p.monthly.base)}
              <small style={{ fontSize: 13, fontWeight: 500, color: 'var(--muted)' }}>
                {product === 'annual' ? ' a year' : ' a month'} + GST
              </small>
            </span>
            {product === 'annual' && yearlySaving(p) > 0 && (
              <span style={{ fontSize: 13.5, fontWeight: 650, color: 'var(--profit)' }}>
                You save {INR(yearlySaving(p))} a year{' '}<br />over paying monthly
              </span>
            )}
            <span style={{ fontSize: 13.5, color: 'var(--muted)' }}>
              {p.accountCap === 1 ? 'One connected account' : `Up to ${p.accountCap} connected accounts`}
            </span>
          </button>
        ))}
      </div>

      <div role="radiogroup" aria-label="How often you pay" style={{ display: 'flex', gap: 10, margin: '0 0 18px' }}>
        {['monthly', 'annual'].map((v) => (
          <button key={v} role="radio" aria-checked={product === v} onClick={() => setProduct(v)}
            style={product === v ? primaryBtn : ghostBtn}>
            {v === 'monthly' ? 'Monthly' : `Yearly${savingLabel(plans)}`}
          </button>
        ))}
      </div>

      <label style={{ display: 'block', margin: '0 0 16px' }}>
        <span style={label}>Where you are billed</span>
        {/* Read-only while a valid GSTIN is given: a registered business is billed where it is registered. */}
        <select ref={stateSelect} aria-invalid={missing === 'state' || undefined} disabled={gstinOk(gstin) && !!(states && states[gstin.slice(0, 2)])}
          style={missing === 'state' ? { ...field, border: '1px solid var(--loss)' } : field} value={billingState}
          onChange={(e) => { setBillingState(e.target.value); if (missing === 'state' && e.target.value) setMissing(null); }}>
          <option value="">Choose your state</option>
          {Object.entries(states || {}).sort((a, b) => String(a[1]).localeCompare(String(b[1])))
            .map(([code, name]) => <option key={code} value={code}>{name}</option>)}
        </select>
        <small style={{ ...note, display: 'block', marginTop: 8 }}>
          This decides how the tax is split on your invoice. The total is the same either way.
        </small>
      </label>

      <label style={{ display: 'block', margin: '0 0 18px' }}>
        <span style={label}>Buying for a business? Enter GSTIN (optional)</span>
        <input ref={gstinInput} style={missing === 'gstin' ? { ...field, border: '1px solid var(--loss)' } : field}
          aria-invalid={missing === 'gstin' || undefined} value={gstin} maxLength={15}
          onChange={(e) => {
            const g = e.target.value.toUpperCase().replace(/\s+/g, '');
            setGstin(g);
            if (missing === 'gstin' && (!g || gstinOk(g))) setMissing(null);
            // A registered business is billed where it is registered: the GSTIN's state is the place of supply.
            if (gstinOk(g) && states && states[g.slice(0, 2)]) setBillingState(g.slice(0, 2));
          }}
          placeholder="Only if you are claiming GST input credit" />
        {gstinOk(gstin) && states && states[gstin.slice(0, 2)] && (
          <small style={{ ...note, display: 'block', marginTop: 8 }}>
            Registered in {states[gstin.slice(0, 2)]}: your invoice is billed there.
          </small>
        )}
      </label>

      {gstin && (
        <>
          <label style={{ display: 'block', margin: '0 0 18px' }}>
            <span style={label}>Legal name of the business</span>
            <input ref={legalInput} style={missing === 'legalName' ? { ...field, border: '1px solid var(--loss)' } : field}
              aria-invalid={missing === 'legalName' || undefined} value={legalName} maxLength={200}
              onChange={(e) => { setLegalName(e.target.value); if (missing === 'legalName' && e.target.value.trim().length >= 2) setMissing(null); }}
              placeholder="As on your GST registration" />
          </label>
          <label style={{ display: 'block', margin: '0 0 18px' }}>
            <span style={label}>Registered address</span>
            <textarea ref={addressInput} rows={2} style={missing === 'address' ? { ...field, border: '1px solid var(--loss)', resize: 'vertical' } : { ...field, resize: 'vertical' }}
              aria-invalid={missing === 'address' || undefined} value={address} maxLength={400}
              onChange={(e) => { setAddress(e.target.value); if (missing === 'address' && e.target.value.trim().length >= 10) setMissing(null); }}
              placeholder="Address on your GST registration, with PIN code" />
          </label>
        </>
      )}

      <label style={{ display: 'block', margin: '0 0 18px' }}>
        <span style={label}>Promo code (optional)</span>
        <input style={field} value={promo} maxLength={24} aria-label="Promo code"
          onChange={(e) => setPromo(e.target.value.toUpperCase())} placeholder="If you have one" />
      </label>

      {/* Always pressable: a greyed button never says what it is waiting for. Pressing it says, and points there. */}
      {missing && (
        <p role="alert" style={{ ...note, color: 'var(--loss)', margin: '0 0 12px' }}>
          {{ plan: 'Pick a plan above to see the total.',
            state: 'Choose the state you are billed in to see the total.',
            gstin: 'That GSTIN does not look right. Check it, or clear the box if you are not claiming GST credit.',
            legalName: 'Enter the legal name your GSTIN is registered under.',
            address: 'Enter the address your GSTIN is registered at.' }[missing]}
        </p>
      )}
      <button style={{ ...primaryBtn, opacity: busy ? 0.55 : 1 }} disabled={busy}
        onClick={() => {
          if (!plan) { setMissing('plan'); planGroup.current?.querySelector('[role="radio"]')?.focus(); return; }
          if (!billingState) { setMissing('state'); stateSelect.current?.focus(); return; }
          if (gstin && !gstinOk(gstin)) { setMissing('gstin'); gstinInput.current?.focus(); return; }
          if (gstin && legalName.trim().length < 2) { setMissing('legalName'); legalInput.current?.focus(); return; }
          if (gstin && address.trim().length < 10) { setMissing('address'); addressInput.current?.focus(); return; }
          setMissing(null);
          onQuote({ plan, product, billingState, promo: promo.trim() || undefined,
            ...(gstin ? { gstin, legalName: legalName.trim(), address: address.trim() } : { gstin: undefined }) });
        }}>
        {busy ? 'Working…' : 'See the total'}
      </button>
    </section>
  );
}

/**
 * Arrow keys move between radios, which the pattern requires and a plain list of buttons does
 * not give you: without it every card is its own tab stop and the group reads as unrelated.
 */
function arrowKeys(e, plans, current, setPlan) {
  const keys = plans.map((p) => p.key);
  const at = Math.max(0, keys.indexOf(current));
  let next = null;
  if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = keys[(at + 1) % keys.length];
  if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = keys[(at - 1 + keys.length) % keys.length];
  if (!next) return;
  e.preventDefault();
  setPlan(next);
  const el = e.currentTarget.parentElement.querySelectorAll('[role="radio"]')[keys.indexOf(next)];
  if (el) el.focus();
}

/** Rupees a year saved by paying yearly instead of 12 monthly payments, before GST like the prices shown. */
export function yearlySaving(p) {
  if (!p || !p.monthly || !p.annual) return 0;
  return Math.round((Number(p.monthly.base) * 12 - Number(p.annual.base)) * 100) / 100;
}

/** Only claims a saving when every plan really has the same one. */
function savingLabel(plans) {
  const pct = plans[0] && plans[0].annual && plans[0].annual.savingPct;
  return pct && plans.every((p) => p.annual && p.annual.savingPct === pct) ? ` — save ${pct}%` : '';
}
